"use client";

import * as React from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { ChevronsUpDown, Loader2, X, Check } from "lucide-react";
import userService, { type UserRole } from "@/services/userService";
import type { User } from "@/types/auth";
import { cn } from "@/lib/utils";

type Props = {
  role?: UserRole;              
  value: string[];              
  onChange: (ids: string[], users?: User[]) => void;
  placeholder?: string;
  disabled?: boolean;
  pageSize?: number;             
  className?: string;
};

function userLabel(u: User) {
  const name = [u.firstName ?? "", u.lastName ?? ""].join(" ").trim();
  return name ? `${name} — ${u.email}` : u.email ?? u.id;
}

export const AsyncUsersMultiSelect: React.FC<Props> = ({
  role,
  value,
  onChange,
  placeholder = "Search and select…",
  disabled,
  pageSize = 20,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<User[]>([]);
  const [selectedMap, setSelectedMap] = React.useState<Record<string, User>>({});

  // pagination & debounce
  const pageRef = React.useRef(1);
  const hasMoreRef = React.useRef(true);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    (async () => {
      const missing = value.filter((id) => !selectedMap[id]);
      if (missing.length === 0) return;
      const users = await userService.getByIds(missing);
      if (users.length) {
        setSelectedMap((prev) => {
          const next = { ...prev };
          for (const u of users) next[u.id] = u;
          return next;
        });
      }
    })();
  }, [value]);

  const handleChipRemove = React.useCallback(
    (e: React.MouseEvent | React.MouseEvent<Element> | React.KeyboardEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      onChange(value.filter((v) => v !== id));
    },
    [onChange, value]
  );

  const runSearch = React.useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const page = reset ? 1 : pageRef.current;
        const { users, hasMore, nextPage } = await userService.search({
          q,
          role,
          page,
          pageSize,
        });

        const filtered = role ? users.filter((u) => u.role === role) : users;
        setItems((prev) => (reset ? filtered : [...prev, ...filtered]));
      pageRef.current = nextPage ?? (hasMore ? page + 1 : page);
        hasMoreRef.current = hasMore;
      } finally {
        setLoading(false);
      }
    },
    [q, role, pageSize]
  );

  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(true), 250);
  }, [open, q, runSearch]);

  const toggle = (u: User) => {
    const exists = value.includes(u.id);
    let nextIds: string[];
    if (exists) {
      nextIds = value.filter((id) => id !== u.id);
    } else {
      nextIds = [...value, u.id];
    }
    // keep label cache fresh
    setSelectedMap((prev) => ({ ...prev, [u.id]: u }));
    onChange(nextIds, nextIds.map((id) => selectedMap[id] ?? (id === u.id ? u : undefined)).filter(Boolean) as User[]);
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const chips = value.map((id) => selectedMap[id]).filter(Boolean) as User[];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between rounded-xl h-auto min-h-10 px-3 py-2",
            className
          )}
        >
          <div className="flex flex-wrap items-center gap-2 text-left">
            {chips.length ? (
             chips.map((u) => (
          <Badge key={u.id} variant="secondary" className="gap-1">
          {userLabel(u)}
          <span
          role="button"
          aria-label={`Remove ${userLabel(u)}`}
          tabIndex={0}
          className="ml-1 inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring h-4 w-4"
          onMouseDown={(e) => handleChipRemove(e, u.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleChipRemove(e, u.id);
          }}
        >
          <X className="h-3 w-3 pointer-events-none" />
        </span>
      </Badge>
    ))
  ) : (
      <span className="text-muted-foreground">{placeholder}</span>
       )}
      </div>
          <div className="flex items-center gap-2 pl-3">
            {value.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={clearAll}
              >
                Clear
              </Button>
            )}
            <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[620px]" align="start">
        <Command shouldFilter={false}>
          <div className="px-2 pt-2">
            <CommandInput
              placeholder="Search…"
              value={q}
              onValueChange={setQ}
              autoFocus
            />
          </div>
          <CommandEmpty>{loading ? "Searching…" : "No users found"}</CommandEmpty>
          <CommandList>
            <CommandGroup>
              {items.map((u) => {
                const checked = value.includes(u.id);
                return (
                  <CommandItem
                    key={u.id}
                    value={u.id}
                    onSelect={() => toggle(u)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        checked ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {userLabel(u)}
                  </CommandItem>
                );
              })}
              {hasMoreRef.current && (
                <div className="flex items-center justify-center py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => runSearch(false)}
                    disabled={loading}
                    className="text-muted-foreground"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Load more
                  </Button>
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AsyncUsersMultiSelect;
